import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { engine, VOICE_ORDER, VOICE_LABELS } from "@/lib/drumAudio";
import QRCode from "qrcode";
import { toast } from "sonner";
import { Play, Square, Circle, Eraser, FileMusic, Magnet, Sparkles, Save, Plus, Trash2, Waves, Copy, GripVertical, FolderOpen, Download, Music2, Share2, Check, Image as ImageIcon } from "lucide-react";

const PAD_COLORS = {
  CC: "#FF3366", RD: "#FF8A00", HH: "#FFD400", HT: "#00F0FF",
  MT: "#00C2FF", FT: "#4D7CFF", SD: "#F0F6FC", BD: "#A855F7",
  CB: "#34D399", TM: "#A3E635", CP: "#F472B6", RS: "#2DD4BF", RB: "#FDE047",
};

const PRESETS = [
  { label: "4/4", beats: 4, den: 4 },
  { label: "3/4", beats: 3, den: 4 },
  { label: "6/8", beats: 6, den: 8 },
  { label: "5/4", beats: 5, den: 4 },
  { label: "7/8", beats: 7, den: 8 },
  { label: "12/8", beats: 12, den: 8 },
  { label: "Custom", beats: 0, den: 0 },
];

const TEMPLATES = {
  rock: { label: "Rock", beats: 4, den: 4, bars: 1, bpm: 110, hits: { BD: [0, 8], SD: [4, 12], HH: [0, 2, 4, 6, 8, 10, 12, 14] } },
  funk: { label: "Funk", beats: 4, den: 4, bars: 1, bpm: 100, hits: { BD: [0, 6, 10], SD: [4, 12], HH: [0, 2, 4, 6, 8, 10, 12, 14] } },
  shuffle: { label: "Shuffle", beats: 12, den: 8, bars: 1, bpm: 90, hits: { BD: [0, 12], SD: [6, 18], HH: [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22] } },
};

// Multi-part ready-made songs. groove = single-bar hit indices (tiled across the part's bars).
const SONG_KITS = {
  pop: {
    label: "Pop", beats: 4, den: 4, bpm: 104,
    parts: [
      { name: "Intro", bars: 1, groove: { HH: [0, 2, 4, 6, 8, 10, 12, 14], BD: [0, 8] } },
      { name: "Verse", bars: 2, groove: { HH: [0, 2, 4, 6, 8, 10, 12, 14], BD: [0, 8], SD: [4, 12] } },
      { name: "Chorus", bars: 2, groove: { CC: [0], HH: [2, 4, 6, 8, 10, 12, 14], BD: [0, 6, 8, 14], SD: [4, 12] } },
      { name: "Outro", bars: 1, groove: { CC: [0], BD: [0], SD: [12] } },
    ],
  },
  rock: {
    label: "Rock", beats: 4, den: 4, bpm: 120,
    parts: [
      { name: "Verse", bars: 2, groove: { HH: [0, 2, 4, 6, 8, 10, 12, 14], BD: [0, 8, 10], SD: [4, 12] } },
      { name: "Chorus", bars: 2, groove: { CC: [0, 8], BD: [0, 4, 8, 12], SD: [4, 12] } },
      { name: "Bridge", bars: 2, groove: { RD: [0, 2, 4, 6, 8, 10, 12, 14], BD: [0, 8], SD: [4, 12] } },
      { name: "Chorus", bars: 2, groove: { CC: [0, 8], BD: [0, 4, 8, 12], SD: [4, 12] } },
    ],
  },
  ballad: {
    label: "Ballad", beats: 4, den: 4, bpm: 72,
    parts: [
      { name: "Verse", bars: 2, groove: { HH: [0, 4, 8, 12], BD: [0], SD: [8] } },
      { name: "Chorus", bars: 2, groove: { RD: [0, 4, 8, 12], BD: [0, 8], SD: [4, 12] } },
      { name: "Outro", bars: 1, groove: { CC: [0], BD: [0] } },
    ],
  },
  reggae: {
    label: "Reggae", beats: 4, den: 4, bpm: 78,
    parts: [
      { name: "Intro", bars: 1, groove: { HH: [2, 6, 10, 14], RS: [4, 12] } },
      { name: "Verse", bars: 2, groove: { HH: [2, 6, 10, 14], SD: [8], BD: [8] } },
      { name: "Chorus", bars: 2, groove: { HH: [0, 2, 4, 6, 8, 10, 12, 14], SD: [4, 12], BD: [0, 8] } },
    ],
  },
  hiphop: {
    label: "Hip-Hop", beats: 4, den: 4, bpm: 92,
    parts: [
      { name: "Intro", bars: 1, groove: { HH: [0, 2, 4, 6, 8, 10, 12, 14], BD: [0], SD: [8] } },
      { name: "Verse", bars: 2, groove: { HH: [0, 2, 4, 6, 8, 10, 12, 14], BD: [0, 7, 10], SD: [4, 12] } },
      { name: "Hook", bars: 2, groove: { CP: [4, 12], HH: [0, 2, 4, 6, 8, 10, 12, 14], BD: [0, 3, 8, 10], SD: [4, 12] } },
    ],
  },
  metal: {
    label: "Metal", beats: 4, den: 4, bpm: 160,
    parts: [
      { name: "Verse", bars: 2, groove: { HH: [0, 2, 4, 6, 8, 10, 12, 14], BD: [0, 2, 4, 6, 8, 10, 12, 14], SD: [4, 12] } },
      { name: "Chorus", bars: 2, groove: { CC: [0, 4, 8, 12], BD: [0, 2, 4, 6, 8, 10, 12, 14], SD: [4, 12] } },
      { name: "Breakdown", bars: 2, groove: { CC: [0, 8], BD: [0, 1, 6, 8, 9, 14], SD: [4, 12] } },
    ],
  },
  jazz: {
    label: "Jazz", beats: 4, den: 4, bpm: 130,
    parts: [
      { name: "Head", bars: 2, groove: { RD: [0, 6, 8, 14], HH: [4, 12], BD: [0], RS: [4, 12] } },
      { name: "Solo", bars: 2, groove: { RD: [0, 6, 8, 14], HH: [4, 12], SD: [2, 10], BD: [0, 8] } },
      { name: "Trade", bars: 2, groove: { SD: [0, 3, 6, 9, 12, 14], BD: [0, 8], CC: [0] } },
    ],
  },
  latin: {
    label: "Latin", beats: 4, den: 4, bpm: 112,
    parts: [
      { name: "Intro", bars: 1, groove: { CB: [0, 4, 8, 12], BD: [0, 6, 10] } },
      { name: "Verse", bars: 2, groove: { CB: [0, 4, 8, 12], HH: [2, 6, 10, 14], BD: [0, 6, 10], RS: [3, 7, 11, 14] } },
      { name: "Montuno", bars: 2, groove: { CB: [0, 2, 4, 6, 8, 10, 12, 14], BD: [0, 6, 10], SD: [4, 12], TM: [2, 10] } },
    ],
  },
  punk: {
    label: "Punk", beats: 4, den: 4, bpm: 180,
    parts: [
      { name: "Verse", bars: 2, groove: { HH: [0, 2, 4, 6, 8, 10, 12, 14], BD: [0, 4, 8, 12], SD: [2, 6, 10, 14] } },
      { name: "Chorus", bars: 2, groove: { CC: [0, 2, 4, 6, 8, 10, 12, 14], BD: [0, 4, 8, 12], SD: [2, 6, 10, 14] } },
      { name: "Outro", bars: 1, groove: { CC: [0], BD: [0, 4, 8, 12], SD: [0, 4, 8, 12] } },
    ],
  },
};

const stepsPerBeatFor = (den) => (den === 8 ? 2 : 4);

const kitPartToMeasures = (beats, den, bars, groove) => {
  const spm = beats * stepsPerBeatFor(den);
  const measures = [];
  for (let b = 0; b < bars; b++) {
    const lanes = {};
    VOICE_ORDER.forEach((v) => {
      const arr = groove[v] || [];
      let s = "";
      for (let i = 0; i < spm; i++) s += arr.includes(i) ? "x" : ".";
      lanes[v] = s;
    });
    measures.push({ lanes });
  }
  return measures;
};

function emptyGrid(beats, den, bars) {
  const total = bars * beats * stepsPerBeatFor(den);
  const g = {};
  VOICE_ORDER.forEach((v) => { g[v] = Array(total).fill(false); });
  return g;
}

export default function PatternBuilder({ onLoadTab, isPro, onUpgrade, onSaved }) {
  const [preset, setPreset] = useState("4/4");
  const [beats, setBeats] = useState(4);
  const [den, setDen] = useState(4);
  const [bars, setBars] = useState(2);
  const [bpm, setBpm] = useState(100);
  const [title, setTitle] = useState("My Pattern");
  const [grid, setGrid] = useState(() => emptyGrid(4, 4, 2));
  const [playing, setPlaying] = useState(false);
  const [recording, setRecording] = useState(false);
  const [playhead, setPlayhead] = useState(-1);
  const [quantize, setQuantize] = useState(false);
  const [countIn, setCountIn] = useState(0);
  const [swing, setSwing] = useState(0);
  const [vols, setVols] = useState(() => Object.fromEntries(VOICE_ORDER.map((v) => [v, 1])));
  const [mutes, setMutes] = useState(() => Object.fromEntries(VOICE_ORDER.map((v) => [v, false])));
  const [solos, setSolos] = useState(() => Object.fromEntries(VOICE_ORDER.map((v) => [v, false])));
  const [songSections, setSongSections] = useState([]);
  const [savedTabs, setSavedTabs] = useState([]);
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [loadedTabId, setLoadedTabId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportLoops, setExportLoops] = useState(1);
  const [fadeOut, setFadeOut] = useState(false);
  const [shareResult, setShareResult] = useState(null);
  const [previewVol, setPreviewVol] = useState(0.5);
  const [songPlaying, setSongPlaying] = useState(false);
  const [songPartIdx, setSongPartIdx] = useState(-1);
  const [previewStep, setPreviewStep] = useState(-1);
  const [partCountIn, setPartCountIn] = useState(true);
  const [recentShares, setRecentShares] = useState([]);

  const previewRef = useRef(null);
  const previewVolRef = useRef(0.5);
  const songNodesRef = useRef([]);
  const songTimersRef = useRef([]);
  const songRafRef = useRef(null);
  const songTimelineRef = useRef([]);
  const songStartRef = useRef(0);
  const dragIndexRef = useRef(null);
  const stepRef = useRef(0);
  const intervalRef = useRef(null);
  const countTimerRef = useRef(null);
  const gridRef = useRef(grid);
  const recRef = useRef(false);
  const quantizeRef = useRef(false);
  const pendingGridRef = useRef(null);
  const swingRef = useRef(0);
  const volsRef = useRef(vols);
  const mutesRef = useRef(mutes);
  const solosRef = useRef(solos);

  const stepsPerBeat = stepsPerBeatFor(den);
  const spm = beats * stepsPerBeat;
  const totalSteps = bars * spm;

  useEffect(() => { gridRef.current = grid; }, [grid]);
  useEffect(() => { quantizeRef.current = quantize; }, [quantize]);
  useEffect(() => { swingRef.current = swing; }, [swing]);
  useEffect(() => { volsRef.current = vols; }, [vols]);
  useEffect(() => { mutesRef.current = mutes; }, [mutes]);
  useEffect(() => { solosRef.current = solos; }, [solos]);
  useEffect(() => { previewVolRef.current = previewVol; }, [previewVol]);

  const stop = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (countTimerRef.current) clearTimeout(countTimerRef.current);
    intervalRef.current = null;
    countTimerRef.current = null;
    recRef.current = false;
    stepRef.current = 0;
    setPlaying(false);
    setRecording(false);
    setPlayhead(-1);
    setCountIn(0);
  }, []);

  // Rebuild grid whenever structure changes (honouring a pending template grid).
  useEffect(() => {
    stop();
    if (pendingGridRef.current) {
      setGrid(pendingGridRef.current);
      pendingGridRef.current = null;
    } else {
      setGrid(emptyGrid(beats, den, bars));
    }
  }, [beats, den, bars, stop]);

  useEffect(() => () => stop(), [stop]);
  useEffect(() => () => stopPreview(), []);

  const runClock = (record) => {
    recRef.current = record;
    setRecording(record);
    setPlaying(true);
    stepRef.current = -1;
    const stepDur = (60 / bpm) * (beats / spm) * 1000;
    intervalRef.current = setInterval(() => {
      stepRef.current = (stepRef.current + 1) % totalSteps;
      const s = stepRef.current;
      setPlayhead(s);
      if (record && s % stepsPerBeat === 0) engine.click(null, s % spm === 0);
      const sps = stepDur / 1000;
      const eighthSteps = Math.max(1, Math.round(stepsPerBeat / 2));
      const eighthIndex = (s % stepsPerBeat) / eighthSteps;
      const off = (Number.isInteger(eighthIndex) && eighthIndex % 2 === 1)
        ? (swingRef.current / 100) * ((eighthSteps * sps) / 3)
        : 0;
      const t = off ? engine.now + off : null;
      const anySolo = Object.values(solosRef.current).some(Boolean);
      VOICE_ORDER.forEach((v) => {
        const cell = gridRef.current[v][s];
        if (!cell) return;
        if (mutesRef.current[v]) return;
        if (anySolo && !solosRef.current[v]) return;
        const accent = s % stepsPerBeat === 0;
        const gain = volsRef.current[v] != null ? volsRef.current[v] : 1;
        if (gain <= 0) return;
        if (v === "HH" && cell === "open") engine.trigger("OH", t, accent, gain);
        else engine.trigger(v, t, accent, gain);
      });
    }, stepDur);
  };

  const start = (record) => {
    engine.ensure();
    stopSong();
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (countTimerRef.current) clearTimeout(countTimerRef.current);
    if (record) {
      // One-bar count-in before capture begins.
      setPlaying(true);
      setRecording(true);
      recRef.current = false; // not capturing during count-in
      const beatMs = 60000 / bpm;
      const doClick = (i) => {
        engine.ensure();
        engine.click(null, i === 0);
        setCountIn(i + 1);
        if (i + 1 < beats) {
          countTimerRef.current = setTimeout(() => doClick(i + 1), beatMs);
        } else {
          countTimerRef.current = setTimeout(() => { setCountIn(0); runClock(true); }, beatMs);
        }
      };
      doClick(0);
    } else {
      runClock(false);
    }
  };

  const toggleCell = (v, idx) => {
    setGrid((g) => {
      const row = g[v].slice();
      if (v === "HH") row[idx] = row[idx] === false ? true : row[idx] === true ? "open" : false;
      else row[idx] = !row[idx];
      return { ...g, [v]: row };
    });
  };

  const tapPad = (key) => {
    engine.ensure();
    engine.trigger(key === "OH" ? "OH" : key, null, true);
    if (recRef.current) {
      let s = stepRef.current < 0 ? 0 : stepRef.current;
      if (quantizeRef.current) s = (Math.round(s / stepsPerBeat) * stepsPerBeat) % totalSteps;
      const voice = key === "OH" ? "HH" : key;
      const val = key === "OH" ? "open" : true;
      setGrid((g) => {
        const row = g[voice].slice();
        row[s] = val;
        return { ...g, [voice]: row };
      });
    }
  };

  const applyPreset = (label) => {
    setPreset(label);
    const p = PRESETS.find((x) => x.label === label);
    if (p && p.beats) { setBeats(p.beats); setDen(p.den); }
  };

  const applyTemplate = (key) => {
    const t = TEMPLATES[key];
    if (!t) return;
    stop();
    const g = emptyGrid(t.beats, t.den, t.bars);
    Object.entries(t.hits).forEach(([v, idxs]) => idxs.forEach((i) => { if (i < g[v].length) g[v][i] = true; }));
    const changed = t.beats !== beats || t.den !== den || t.bars !== bars;
    pendingGridRef.current = g;
    setPreset(`${t.beats}/${t.den}`);
    setBeats(t.beats); setDen(t.den); setBars(t.bars); setBpm(t.bpm);
    if (!changed) { setGrid(g); pendingGridRef.current = null; }
    toast.success(`${t.label} groove loaded`);
  };

  const buildMeasures = () => {
    const measures = [];
    for (let b = 0; b < bars; b++) {
      const lanes = {};
      VOICE_ORDER.forEach((v) => {
        let s = "";
        for (let i = 0; i < spm; i++) {
          const cell = grid[v][b * spm + i];
          s += v === "HH" ? (cell === true ? "x" : cell === "open" ? "o" : ".") : (cell ? "x" : ".");
        }
        lanes[v] = s;
      });
      measures.push({ lanes });
    }
    return measures;
  };

  const tabMeta = () => {
    const anySolo = Object.values(solos).some(Boolean);
    return {
      title: title.trim() || "My Pattern",
      artist: "Created by you",
      bpm,
      time_signature: `${beats}/${den}`,
      key: "—",
      difficulty: "Custom",
      steps_per_measure: spm,
      notes: "Your own pattern — built in the Virtual Kit.",
      mix: {
        mixer: Object.fromEntries(VOICE_ORDER.map((v) => [v, {
          vol: vols[v] != null ? vols[v] : 1,
          mute: !!mutes[v] || (anySolo && !solos[v]),
        }])),
        swing,
      },
    };
  };

  const buildTab = () => ({ ...tabMeta(), sections: [{ name: "Pattern", measures: buildMeasures() }] });
  const buildSongTab = () => ({
    ...tabMeta(),
    sections: songSections.map((sec) => {
      const reps = Math.max(1, sec.repeat || 1);
      const measures = [];
      for (let r = 0; r < reps; r++) measures.push(...sec.measures);
      return { name: sec.name, measures, bpm: sec.bpm || bpm, ...(sec.ramp ? { ramp: sec.ramp, rampCurve: sec.rampCurve || "linear" } : {}) };
    }),
  });

  const currentHasHits = () => VOICE_ORDER.some((v) => grid[v].some(Boolean));

  const addToSong = () => {
    if (!currentHasHits()) { toast.message("Add a few hits first!"); return; }
    setSongSections((s) => [...s, { name: `Part ${s.length + 1}`, measures: buildMeasures(), repeat: 1, bpm, ramp: null }]);
    toast.success("Added to your song arrangement");
  };

  const duplicatePart = (i) => {
    setSongSections((s) => {
      const c = [...s];
      c.splice(i + 1, 0, { name: `${s[i].name} copy`, measures: s[i].measures.map((m) => ({ ...m })), repeat: s[i].repeat || 1, bpm: s[i].bpm, ramp: s[i].ramp || null, rampCurve: s[i].rampCurve || "linear" });
      return c;
    });
    toast.success("Part duplicated");
  };

  const onPartDrop = (i) => {
    const from = dragIndexRef.current;
    dragIndexRef.current = null;
    if (from == null || from === i) return;
    setSongSections((s) => {
      const c = [...s];
      const [moved] = c.splice(from, 1);
      c.splice(i, 0, moved);
      return c;
    });
  };

  const parseTimeSig = (ts) => {
    const [b, d] = String(ts || "4/4").split("/").map((n) => parseInt(n, 10));
    return { b: b || 4, d: d === 8 ? 8 : 4 };
  };

  const measuresToGrid = (measures, spmVal) => {
    const g = {};
    const total = measures.length * spmVal;
    VOICE_ORDER.forEach((v) => {
      const row = Array(total).fill(false);
      measures.forEach((m, mi) => {
        const lane = (m.lanes && m.lanes[v]) || "";
        for (let i = 0; i < spmVal; i++) {
          const ch = lane[i];
          if (ch === "x") row[mi * spmVal + i] = true;
          else if (ch === "o") row[mi * spmVal + i] = "open";
        }
      });
      g[v] = row;
    });
    return g;
  };

  const openPresets = async () => {
    if (!isPro) {
      toast.message("Loading saved patterns is a Pro feature");
      if (onUpgrade) onUpgrade();
      return;
    }
    try {
      const { data } = await api.get("/tabs");
      setSavedTabs(data);
      setPresetsOpen(true);
    } catch (e) {
      toast.error("Could not load your library");
    }
  };

  const loadSavedTab = (item) => {
    const t = item.tab || item;
    stop();
    const { b, d } = parseTimeSig(t.time_signature);
    const spmVal = t.steps_per_measure || (b * stepsPerBeatFor(d));
    const secs = Array.isArray(t.sections) ? t.sections : [];
    const first = secs[0] || { measures: [] };
    const firstBars = Math.max(1, Math.min(4, first.measures.length || 1));
    const mx = (t.mix && t.mix.mixer) || {};
    setVols(Object.fromEntries(VOICE_ORDER.map((v) => [v, mx[v] && typeof mx[v].vol === "number" ? mx[v].vol : 1])));
    setMutes(Object.fromEntries(VOICE_ORDER.map((v) => [v, !!(mx[v] && mx[v].mute)])));
    setSolos(Object.fromEntries(VOICE_ORDER.map((v) => [v, false])));
    setSwing(t.mix && typeof t.mix.swing === "number" ? t.mix.swing : 0);
    setBpm(t.bpm || 100);
    setTitle(t.title || "My Pattern");
    const g = measuresToGrid(first.measures.slice(0, firstBars), spmVal);
    const changed = b !== beats || d !== den || firstBars !== bars;
    pendingGridRef.current = g;
    setPreset(`${b}/${d}`);
    setBeats(b); setDen(d); setBars(firstBars);
    if (!changed) { setGrid(g); pendingGridRef.current = null; }
    setSongSections(secs.length > 1 ? secs.map((s, i) => ({ name: s.name || `Part ${i + 1}`, measures: s.measures, repeat: 1, bpm: s.bpm || t.bpm || 100, ramp: s.ramp || null, rampCurve: s.rampCurve || "linear" })) : []);
    setLoadedTabId(item.id || null);
    setPresetsOpen(false);
    toast.success(`Loaded "${t.title || "pattern"}"`);
  };

  const stopPreview = () => {
    if (previewRef.current) {
      clearInterval(previewRef.current);
      previewRef.current = null;
    }
  };

  const previewKit = (key) => {
    if (playing || recording) return;
    const k = SONG_KITS[key];
    if (!k) return;
    stopPreview();
    engine.ensure();
    const spb = stepsPerBeatFor(k.den);
    const spmV = k.beats * spb;
    const measures = kitPartToMeasures(k.beats, k.den, k.parts[0].bars, k.parts[0].groove);
    const rows = {};
    VOICE_ORDER.forEach((v) => { rows[v] = measures.map((m) => m.lanes[v] || "").join(""); });
    const total = measures.length * spmV;
    const stepDur = (60 / k.bpm) * (k.beats / spmV) * 1000;
    let s = -1;
    previewRef.current = setInterval(() => {
      s = (s + 1) % total;
      VOICE_ORDER.forEach((v) => {
        if (rows[v][s] === "x") engine.trigger(v, null, s % spb === 0, previewVolRef.current);
      });
    }, stepDur);
  };

  const applySongKit = (key) => {
    const k = SONG_KITS[key];
    if (!k) return;
    stopPreview();
    stop();
    const spmVal = k.beats * stepsPerBeatFor(k.den);
    const sections = k.parts.map((p) => ({ name: p.name, measures: kitPartToMeasures(k.beats, k.den, p.bars, p.groove), repeat: 1, bpm: p.bpm || k.bpm, ramp: p.ramp || null, rampCurve: "linear" }));
    setVols(Object.fromEntries(VOICE_ORDER.map((v) => [v, 1])));
    setMutes(Object.fromEntries(VOICE_ORDER.map((v) => [v, false])));
    setSolos(Object.fromEntries(VOICE_ORDER.map((v) => [v, false])));
    setSwing(k.swing || 0);
    setBpm(k.bpm);
    setTitle(`${k.label} song`);
    const firstBars = k.parts[0].bars;
    const g = measuresToGrid(sections[0].measures, spmVal);
    const changed = k.beats !== beats || k.den !== den || firstBars !== bars;
    pendingGridRef.current = g;
    setPreset(`${k.beats}/${k.den}`);
    setBeats(k.beats); setDen(k.den); setBars(firstBars);
    if (!changed) { setGrid(g); pendingGridRef.current = null; }
    setSongSections(sections);
    setLoadedTabId(null);
    toast.success(`${k.label} song loaded — ${sections.length} parts`);
  };

  const loopTab = (tab, n) => {
    if (!n || n <= 1) return tab;
    const sections = [];
    for (let r = 0; r < n; r++) sections.push(...tab.sections.map((s) => ({ ...s })));
    return { ...tab, sections };
  };

  const buildPreviewCard = (tab) => {
    const W = 1200, H = 630;
    const c = document.createElement("canvas");
    c.width = W; c.height = H;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#0b0c10"; ctx.fillRect(0, 0, W, H);
    // accent glow
    const grad = ctx.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, "rgba(0,240,255,0.14)");
    grad.addColorStop(1, "rgba(255,171,0,0.10)");
    ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);
    // waveform bars motif
    ctx.fillStyle = "#00f0ff";
    for (let i = 0; i < 26; i++) {
      const bh = 40 + Math.abs(Math.sin(i * 0.9)) * 120;
      ctx.globalAlpha = 0.5;
      ctx.fillRect(70 + i * 42, H - 70 - bh, 16, bh);
    }
    ctx.globalAlpha = 1;
    // brand
    ctx.fillStyle = "#ffab00";
    ctx.font = "700 34px sans-serif";
    ctx.fillText("BEATFLOW AI", 70, 110);
    ctx.fillStyle = "#8b98a9";
    ctx.font = "600 22px sans-serif";
    ctx.fillText("AI DRUM TABS", 70, 145);
    // title
    ctx.fillStyle = "#f0f6fc";
    ctx.font = "800 76px sans-serif";
    const title = (tab.title || "Drum groove").slice(0, 26);
    ctx.fillText(title, 70, 300);
    // artist
    ctx.fillStyle = "#c893ff";
    ctx.font = "600 38px sans-serif";
    ctx.fillText((tab.artist || "Created by you").slice(0, 40), 72, 352);
    // meta chips
    ctx.fillStyle = "#00f0ff";
    ctx.font = "700 30px sans-serif";
    const parts = (tab.sections || []).length;
    ctx.fillText(`${tab.bpm} BPM   ·   ${tab.time_signature}   ·   ${parts} part${parts !== 1 ? "s" : ""}`, 72, 420);
    return c.toDataURL("image/png");
  };

  const exportAudio = async (format = "wav") => {
    if (!isPro) {
      toast.message("Exporting audio is a Pro feature");
      if (onUpgrade) onUpgrade();
      return;
    }
    const song = songSections.length > 0;
    if (!song && !currentHasHits()) { toast.message("Add a few hits first!"); return; }
    setExporting(true);
    try {
      const tab = loopTab(song ? buildSongTab() : buildTab(), exportLoops);
      const opts = fadeOut ? { fadeOut: true, fadeSec: 2.0 } : {};
      const blob = format === "mp3" ? await engine.renderToMp3(tab, opts) : await engine.renderToWav(tab, opts);
      if (!blob) { toast.message("Nothing to render"); return; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const suffix = exportLoops > 1 ? `_x${exportLoops}` : "";
      a.download = `${(tab.title || "pattern").replace(/[^a-z0-9]+/gi, "_")}${suffix}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exported!`);
    } catch (e) {
      toast.error("Could not render audio");
    } finally {
      setExporting(false);
    }
  };

  const shareSong = async () => {
    if (!isPro) {
      toast.message("Public share links are a Pro feature");
      if (onUpgrade) onUpgrade();
      return;
    }
    const song = songSections.length > 0;
    if (!song && !currentHasHits()) { toast.message("Add a few hits first!"); return; }
    try {
      const tab = song ? buildSongTab() : buildTab();
      const preview = buildPreviewCard(tab);
      const { data } = await api.post("/share", { tab, preview, origin: window.location.origin });
      const url = `${window.location.origin}/api/share/${data.id}/view`;
      let qr = "";
      try { qr = await QRCode.toDataURL(url, { margin: 1, width: 240, color: { dark: "#0b0c10", light: "#ffffff" } }); } catch (e) { /* ignore */ }
      try { await navigator.clipboard.writeText(url); } catch (e) { /* ignore */ }
      setShareResult({ url, qr });
      fetchRecentShares();
      toast.success("Public link copied — scan or share the QR!");
    } catch (e) {
      toast.error("Could not create share link");
    }
  };

  const fetchRecentShares = async () => {
    try { const { data } = await api.get("/my-shares"); setRecentShares(data); } catch (e) { /* ignore */ }
  };

  const copyShareLink = async () => {
    if (!shareResult) return;
    try { await navigator.clipboard.writeText(shareResult.url); toast.success("Link copied"); } catch (e) { /* ignore */ }
  };

  const downloadPreviewCard = () => {
    if (!isPro) {
      toast.message("Downloading the preview card is a Pro feature");
      if (onUpgrade) onUpgrade();
      return;
    }
    const song = songSections.length > 0;
    if (!song && !currentHasHits()) { toast.message("Add a few hits first!"); return; }
    const tab = song ? buildSongTab() : buildTab();
    const dataUrl = buildPreviewCard(tab);
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `${(tab.title || "pattern").replace(/[^a-z0-9]+/gi, "_")}_card.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Preview card saved!");
  };

  const openInPlayer = () => {
    const song = songSections.length > 0;
    if (!song && !currentHasHits()) { toast.message("Add a few hits first!"); return; }
    stop();
    onLoadTab(song ? buildSongTab() : buildTab());
    toast.success("Loaded into the player");
  };

  const stopSong = () => {
    songNodesRef.current.forEach((n) => { try { n.stop(); } catch (e) { /* noop */ } });
    songNodesRef.current = [];
    songTimersRef.current.forEach((id) => clearTimeout(id));
    songTimersRef.current = [];
    if (songRafRef.current) cancelAnimationFrame(songRafRef.current);
    songRafRef.current = null;
    setSongPlaying(false);
    setSongPartIdx(-1);
    setPreviewStep(-1);
  };

  const playSong = (startIdx = 0) => {
    if (songSections.length === 0) { toast.message("Add parts to your song first!"); return; }
    stop();
    stopSong();
    engine.ensure();
    const anySolo = Object.values(solos).some(Boolean);
    const eighthSteps = Math.max(1, Math.round(stepsPerBeat / 2));
    const nodes = [];
    const timeline = [];
    const start = engine.now + 0.2;
    let tCursor = 0;
    let prevBpm = null;
    for (let idx = startIdx; idx < songSections.length; idx++) {
      const sec = songSections[idx];
      const reps = Math.max(1, sec.repeat || 1);
      const startBpm = sec.bpm || bpm;
      const endBpm = sec.ramp || startBpm;
      const ease = sec.rampCurve === "ease";
      const partSteps = sec.measures.length * spm * reps;
      // Part count-in when the tempo changes from the previous part (if enabled).
      if (partCountIn && prevBpm != null && startBpm !== prevBpm) {
        const beatDur = 60 / startBpm;
        for (let b = 0; b < beats; b++) nodes.push(...engine.click(start + tCursor + b * beatDur, b === 0));
        tCursor += beats * beatDur;
      }
      let k = 0;
      for (let r = 0; r < reps; r++) {
        sec.measures.forEach((m) => {
          for (let i = 0; i < spm; i++) {
            const frac = partSteps > 1 ? k / (partSteps - 1) : 0;
            const ef = ease ? frac * frac * (3 - 2 * frac) : frac;
            const stepBpm = startBpm + (endBpm - startBpm) * ef;
            const stepDur = (60 / stepBpm) * (beats / spm);
            const swingDelay = (swing / 100) * ((eighthSteps * stepDur) / 3);
            const eighthIndex = (i % stepsPerBeat) / eighthSteps;
            const off = Number.isInteger(eighthIndex) && eighthIndex % 2 === 1 ? swingDelay : 0;
            const at = start + tCursor + off;
            timeline.push({ t: tCursor, part: idx, col: k % totalSteps });
            VOICE_ORDER.forEach((v) => {
              const ch = (m.lanes[v] || "")[i];
              if (ch !== "x" && ch !== "o") return;
              if (mutes[v]) return;
              if (anySolo && !solos[v]) return;
              const gain = vols[v] != null ? vols[v] : 1;
              if (gain <= 0) return;
              const accent = i % stepsPerBeat === 0;
              nodes.push(...engine.trigger(v === "HH" && ch === "o" ? "OH" : v, at, accent, gain));
            });
            tCursor += stepDur;
            k++;
          }
        });
      }
      prevBpm = endBpm;
    }
    songNodesRef.current = nodes;
    songTimelineRef.current = timeline;
    songStartRef.current = start;
    setSongPlaying(true);

    const tick = () => {
      const elapsed = engine.now - songStartRef.current;
      const tl = songTimelineRef.current;
      if (elapsed < 0) { songRafRef.current = requestAnimationFrame(tick); return; }
      if (tl.length === 0 || elapsed > tCursor + 0.4) { stopSong(); return; }
      let idx = 0;
      while (idx < tl.length - 1 && tl[idx + 1].t <= elapsed) idx++;
      setSongPartIdx(tl[idx].part);
      setPreviewStep(tl[idx].col);
      songRafRef.current = requestAnimationFrame(tick);
    };
    songRafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => () => stopSong(), []);

  const saveToLibrary = async (asNew = false) => {
    if (!isPro) {
      toast.message("Saving to your library is a Pro feature");
      if (onUpgrade) onUpgrade();
      return;
    }
    const song = songSections.length > 0;
    if (!song && !currentHasHits()) { toast.message("Add a few hits first!"); return; }
    const tab = song ? buildSongTab() : buildTab();
    try {
      if (loadedTabId && !asNew) {
        const { data } = await api.put(`/tabs/${loadedTabId}`, { tab });
        toast.success("Pattern updated!");
        if (onSaved) onSaved(data);
      } else {
        const { data } = await api.post("/tabs", { tab });
        toast.success("Saved to your library!");
        setLoadedTabId(data.id || null);
        if (onSaved) onSaved(data);
      }
    } catch (e) {
      toast.error("Could not save pattern");
    }
  };

  const pads = [...VOICE_ORDER, "OH"];

  return (
    <div className="relative" data-testid="pattern-builder">
      {countIn > 0 && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-[#0b0c10]/80 rounded-lg pointer-events-none" data-testid="countin-overlay">
          <span key={countIn} className="font-heading text-6xl text-[#FF3366] animate-in zoom-in-50 duration-150">{countIn}</span>
        </div>
      )}

      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="h-9 bg-[#0b0c10] border-white/10 text-sm mb-3 font-heading"
        data-testid="pattern-title-input"
      />

      <div className="flex flex-wrap gap-1.5 mb-2" data-testid="pattern-templates">
        <span className="text-[10px] text-slate-500 font-tab flex items-center gap-1"><Sparkles className="w-3 h-3" /></span>
        {Object.entries(TEMPLATES).map(([k, t]) => (
          <Button key={k} size="sm" variant="outline" onClick={() => applyTemplate(k)}
            className="h-7 px-2.5 text-xs border-white/15 bg-white/5 text-slate-300" data-testid={`template-${k}`}>
            {t.label}
          </Button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="song-kits">
        <span className="text-[10px] text-[#ffab00]/80 font-tab flex items-center gap-1"><Music2 className="w-3 h-3" /> Songs</span>
        {Object.entries(SONG_KITS).map(([k, s]) => (
          <Button key={k} size="sm" variant="outline"
            onClick={() => applySongKit(k)}
            onMouseEnter={() => previewKit(k)}
            onMouseLeave={stopPreview}
            className="h-7 px-2.5 text-xs border-[#ffab00]/30 bg-[#ffab00]/5 text-[#ffab00]" data-testid={`song-kit-${k}`}>
            {s.label}
          </Button>
        ))}
        <span className="flex items-center gap-1 ml-auto" title="Hover-preview volume">
          <Play className="w-3 h-3 text-slate-500 shrink-0" />
          <input
            type="range" min="0" max="100" value={Math.round(previewVol * 100)}
            onChange={(e) => setPreviewVol(parseInt(e.target.value, 10) / 100)}
            className="w-14 h-1 cursor-pointer" style={{ accentColor: "#ffab00" }}
            data-testid="preview-volume-slider"
          />
        </span>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <Select value={preset} onValueChange={applyPreset} disabled={songSections.length > 0}>
          <SelectTrigger className="h-8 w-[88px] bg-white/5 border-white/15 text-xs font-tab" data-testid="pattern-timesig-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#12161f] border-white/10">
            {PRESETS.map((p) => (
              <SelectItem key={p.label} value={p.label} data-testid={`timesig-${p.label}`}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {preset === "Custom" && (
          <div className="flex items-center gap-1" data-testid="custom-timesig">
            <Input
              type="number" min="2" max="12" value={beats}
              onChange={(e) => setBeats(Math.min(12, Math.max(2, parseInt(e.target.value, 10) || 4)))}
              className="h-8 w-14 bg-white/5 border-white/15 text-xs font-tab" data-testid="custom-beats-input"
            />
            <span className="text-slate-500">/</span>
            <Select value={String(den)} onValueChange={(v) => setDen(parseInt(v, 10))}>
              <SelectTrigger className="h-8 w-14 bg-white/5 border-white/15 text-xs font-tab" data-testid="custom-den-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#12161f] border-white/10">
                <SelectItem value="4">4</SelectItem>
                <SelectItem value="8">8</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <Select value={String(bars)} onValueChange={(v) => setBars(parseInt(v, 10))}>
          <SelectTrigger className="h-8 w-[84px] bg-white/5 border-white/15 text-xs font-tab" data-testid="pattern-bars-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#12161f] border-white/10">
            {[1, 2, 3, 4].map((n) => (
              <SelectItem key={n} value={String(n)}>{n} bar{n > 1 ? "s" : ""}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] text-slate-500 font-tab w-8">{bpm}</span>
        <Slider value={[bpm]} min={40} max={220} step={1} onValueChange={(v) => setBpm(v[0])} className="flex-1" data-testid="pattern-bpm-slider" />
      </div>
      <div className="flex items-center gap-2 mb-3">
        <Waves className="w-3.5 h-3.5 text-slate-500 shrink-0" />
        <span className="text-[10px] text-slate-500 font-tab w-8">{swing}%</span>
        <Slider value={[swing]} min={0} max={100} step={5} onValueChange={(v) => setSwing(v[0])} className="flex-1" data-testid="pattern-swing-slider" />
      </div>

      {/* Editable grid */}
      <div className="overflow-x-auto scroll-thin mb-3 rounded-lg bg-[#0d1117] p-2">
        {VOICE_ORDER.map((v) => (
          <div key={v} className="flex items-center mb-0.5">
            <div className="w-8 shrink-0 font-tab text-[10px] font-bold" style={{ color: PAD_COLORS[v] }}>{v}</div>
            <button
              onClick={() => setMutes((o) => ({ ...o, [v]: !o[v] }))}
              className={`w-4 h-4 mr-0.5 shrink-0 rounded text-[8px] font-bold font-tab leading-none ${mutes[v] ? "bg-[#FF3366] text-white" : "bg-white/5 text-slate-500"}`}
              data-testid={`mute-${v}`}
              title={`Mute ${VOICE_LABELS[v]}`}
            >M</button>
            <button
              onClick={() => setSolos((o) => ({ ...o, [v]: !o[v] }))}
              className={`w-4 h-4 mr-1 shrink-0 rounded text-[8px] font-bold font-tab leading-none ${solos[v] ? "bg-[#00f0ff] text-black" : "bg-white/5 text-slate-500"}`}
              data-testid={`solo-${v}`}
              title={`Solo ${VOICE_LABELS[v]}`}
            >S</button>
            <input
              type="range" min="0" max="100"
              value={Math.round((vols[v] != null ? vols[v] : 1) * 100)}
              onChange={(e) => setVols((o) => ({ ...o, [v]: parseInt(e.target.value, 10) / 100 }))}
              className="w-9 h-1 mr-1.5 shrink-0 cursor-pointer"
              style={{ accentColor: PAD_COLORS[v] }}
              data-testid={`vol-${v}`}
              title={`${VOICE_LABELS[v]} volume`}
            />
            <div className="flex">
              {Array.from({ length: totalSteps }).map((_, i) => {
                const cell = grid[v][i];
                const beatStart = i % stepsPerBeat === 0;
                const measStart = i % spm === 0;
                const isHead = i === (songPlaying ? previewStep : playhead);
                const open = cell === "open";
                const testid = v === VOICE_ORDER[0] ? `cell-${i}` : v === "HH" ? `hh-cell-${i}` : undefined;
                return (
                  <button
                    key={i}
                    onClick={() => toggleCell(v, i)}
                    data-testid={testid}
                    className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex items-center justify-center"
                    style={{
                      marginLeft: measStart && i !== 0 ? "4px" : beatStart && i !== 0 ? "2px" : "0px",
                      background: isHead ? "rgba(0,240,255,0.15)" : "transparent",
                    }}
                  >
                    <span
                      className="block rounded-full transition-all"
                      style={{
                        width: cell ? "9px" : "4px",
                        height: cell ? "9px" : "4px",
                        background: open ? "transparent" : cell ? PAD_COLORS[v] : "rgba(255,255,255,0.12)",
                        border: open ? `2px solid ${PAD_COLORS[v]}` : "none",
                      }}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        <p className="text-[9px] text-slate-600 font-tab mt-1.5 pl-8">Tip: click a hi-hat cell twice for an open hat (ring)</p>
      </div>

      {/* Transport */}
      <div className="flex items-center gap-2 mb-2">
        {playing ? (
          <Button onClick={stop} className="h-9 flex-1 bg-[#FF3366] hover:bg-[#ff4d7a] text-white font-semibold" data-testid="pattern-stop-button">
            <Square className="w-4 h-4 mr-1.5" /> Stop
          </Button>
        ) : (
          <>
            <Button onClick={() => start(false)} className="h-9 flex-1 bg-[#00f0ff] hover:bg-[#33f4ff] text-black font-semibold" data-testid="pattern-play-button">
              <Play className="w-4 h-4 mr-1.5" /> Play
            </Button>
            <Button onClick={() => start(true)} variant="outline" className="h-9 border-[#FF3366]/40 text-[#FF3366] bg-[#FF3366]/10" data-testid="pattern-record-button">
              <Circle className="w-4 h-4 mr-1.5" /> Rec
            </Button>
          </>
        )}
        <Button
          onClick={() => setQuantize((q) => !q)}
          variant="outline" size="icon"
          className={`h-9 w-9 border-white/15 ${quantize ? "bg-[#00f0ff]/15 text-[#00f0ff] border-[#00f0ff]/40" : "bg-white/5"}`}
          data-testid="pattern-quantize-toggle"
          title="Tighten recorded taps to the beat"
        >
          <Magnet className="w-4 h-4" />
        </Button>
        <Button onClick={() => setGrid(emptyGrid(beats, den, bars))} variant="outline" size="icon" className="h-9 w-9 border-white/15 bg-white/5" data-testid="pattern-clear-button">
          <Eraser className="w-4 h-4" />
        </Button>
      </div>

      {recording && countIn === 0 && (
        <p className="text-[11px] text-[#FF3366] font-tab mb-2 text-center" data-testid="recording-indicator">
          ● Recording{quantize ? " (quantized)" : ""} — tap the pads below in time
        </p>
      )}

      {/* Recording pads */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        {pads.map((key) => {
          const color = key === "OH" ? PAD_COLORS.HH : PAD_COLORS[key];
          const label = key === "OH" ? "Open Hat" : VOICE_LABELS[key];
          return (
            <button
              key={key}
              onClick={() => tapPad(key)}
              data-testid={`builder-pad-${key}`}
              className="aspect-[5/3] rounded-lg border border-white/10 bg-white/[0.03] flex flex-col items-center justify-center active:scale-95 transition-transform"
            >
              <span className="font-heading text-sm font-bold" style={{ color }}>{key}</span>
              <span className="text-[8px] text-slate-500 font-tab leading-none">{label}</span>
            </button>
          );
        })}
      </div>

      {songSections.length > 0 && (
        <div className="mb-3 rounded-lg bg-white/[0.03] border border-white/10 p-2" data-testid="song-arrangement">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-tab uppercase tracking-widest text-[#ffab00]">Song: {songSections.length} part{songSections.length > 1 ? "s" : ""}</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPartCountIn((c) => !c)}
                className={`text-[10px] font-tab px-2 py-0.5 rounded ${partCountIn ? "bg-white/10 text-slate-200" : "bg-white/5 text-slate-500"}`}
                data-testid="part-countin-toggle"
                title="Play a count-in click when a part changes tempo"
              >
                Count-in {partCountIn ? "on" : "off"}
              </button>
              <button
                onClick={songPlaying ? stopSong : playSong}
                className={`flex items-center gap-1 text-[10px] font-tab px-2 py-0.5 rounded ${songPlaying ? "bg-[#FF3366]/15 text-[#FF3366]" : "bg-[#00f0ff]/15 text-[#00f0ff]"}`}
                data-testid="preview-song-button"
              >
                {songPlaying ? <><Square className="w-3 h-3" /> Stop</> : <><Play className="w-3 h-3" /> Preview song</>}
              </button>
              <button onClick={() => { stopSong(); setSongSections([]); }} className="text-[10px] text-slate-500 hover:text-[#FF3366] font-tab" data-testid="clear-song-button">Clear</button>
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            {songSections.map((sec, i) => (
              <div
                key={i}
                className={`rounded bg-white/5 px-1.5 py-1.5 transition-colors ${songPartIdx === i ? "ring-1 ring-[#00f0ff] bg-[#00f0ff]/10" : ""}`}
                data-testid={`song-part-${i}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onPartDrop(i)}
              >
                <div className="flex items-center gap-1">
                  <span
                    draggable
                    onDragStart={() => { dragIndexRef.current = i; }}
                    onDragEnd={() => { dragIndexRef.current = null; }}
                    className="cursor-grab active:cursor-grabbing text-slate-500 hover:text-white shrink-0"
                    data-testid={`drag-handle-${i}`}
                    title="Drag to reorder"
                  >
                    <GripVertical className="w-3.5 h-3.5" />
                  </span>
                  <Input
                    value={sec.name}
                    onChange={(e) => setSongSections((s) => s.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                    className="h-6 flex-1 min-w-0 bg-[#0b0c10] border-white/10 text-[11px] font-tab px-1.5"
                    data-testid={`section-name-${i}`}
                  />
                  <button onClick={() => playSong(i)} data-testid={`preview-from-part-${i}`} className="shrink-0" title="Preview song from this part">
                    <Play className="w-3.5 h-3.5 text-slate-500 hover:text-[#00f0ff]" />
                  </button>
                  <button onClick={() => duplicatePart(i)} data-testid={`duplicate-section-${i}`} className="shrink-0" title="Duplicate part">
                    <Copy className="w-3.5 h-3.5 text-slate-500 hover:text-[#00f0ff]" />
                  </button>
                  <button onClick={() => setSongSections((s) => s.filter((_, j) => j !== i))} data-testid={`remove-section-${i}`} className="shrink-0" title="Remove part">
                    <Trash2 className="w-3.5 h-3.5 text-slate-500 hover:text-[#FF3366]" />
                  </button>
                </div>
                <div className="flex items-center gap-2 mt-1 pl-5">
                  <div className="flex items-center gap-0.5">
                    <span className="text-[9px] text-slate-500 font-tab mr-0.5">Rep</span>
                    <button
                      onClick={() => setSongSections((s) => s.map((x, j) => j === i ? { ...x, repeat: Math.max(1, (x.repeat || 1) - 1) } : x))}
                      className="w-5 h-5 rounded bg-white/5 text-slate-400 text-xs leading-none hover:text-white"
                      data-testid={`repeat-minus-${i}`}
                    >−</button>
                    <span className="text-[11px] font-tab text-[#ffab00] w-6 text-center" data-testid={`repeat-count-${i}`}>×{sec.repeat || 1}</span>
                    <button
                      onClick={() => setSongSections((s) => s.map((x, j) => j === i ? { ...x, repeat: Math.min(16, (x.repeat || 1) + 1) } : x))}
                      className="w-5 h-5 rounded bg-white/5 text-slate-400 text-xs leading-none hover:text-white"
                      data-testid={`repeat-plus-${i}`}
                    >+</button>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <span className="text-[9px] text-slate-500 font-tab mr-0.5">BPM</span>
                    <button
                      onClick={() => setSongSections((s) => s.map((x, j) => j === i ? { ...x, bpm: Math.max(40, (x.bpm || bpm) - 5) } : x))}
                      className="w-5 h-5 rounded bg-white/5 text-slate-400 text-xs leading-none hover:text-white"
                      data-testid={`part-bpm-minus-${i}`}
                    >−</button>
                    <span className="text-[11px] font-tab text-[#00f0ff] w-8 text-center" data-testid={`part-bpm-${i}`}>{sec.bpm || bpm}</span>
                    <button
                      onClick={() => setSongSections((s) => s.map((x, j) => j === i ? { ...x, bpm: Math.min(240, (x.bpm || bpm) + 5) } : x))}
                      className="w-5 h-5 rounded bg-white/5 text-slate-400 text-xs leading-none hover:text-white"
                      data-testid={`part-bpm-plus-${i}`}
                    >+</button>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <span className="text-[9px] text-slate-500 font-tab mr-0.5">Ramp→</span>
                    {sec.ramp ? (
                      <>
                        <button
                          onClick={() => setSongSections((s) => s.map((x, j) => j === i ? { ...x, ramp: Math.max(40, (x.ramp || x.bpm || bpm) - 5) } : x))}
                          className="w-5 h-5 rounded bg-white/5 text-slate-400 text-xs leading-none hover:text-white"
                          data-testid={`part-ramp-minus-${i}`}
                        >−</button>
                        <span className="text-[11px] font-tab text-[#c893ff] w-8 text-center" data-testid={`part-ramp-${i}`}>{sec.ramp}</span>
                        <button
                          onClick={() => setSongSections((s) => s.map((x, j) => j === i ? { ...x, ramp: Math.min(240, (x.ramp || x.bpm || bpm) + 5) } : x))}
                          className="w-5 h-5 rounded bg-white/5 text-slate-400 text-xs leading-none hover:text-white"
                          data-testid={`part-ramp-plus-${i}`}
                        >+</button>
                        <button
                          onClick={() => setSongSections((s) => s.map((x, j) => j === i ? { ...x, rampCurve: x.rampCurve === "ease" ? "linear" : "ease" } : x))}
                          className={`ml-0.5 text-[9px] font-tab px-1 rounded ${sec.rampCurve === "ease" ? "text-[#c893ff] bg-[#A855F7]/15" : "text-slate-500"}`}
                          data-testid={`part-ramp-curve-${i}`}
                          title="Ramp shape: linear or ease-in/out"
                        >{sec.rampCurve === "ease" ? "Ease" : "Lin"}</button>
                        <button
                          onClick={() => setSongSections((s) => s.map((x, j) => j === i ? { ...x, ramp: null } : x))}
                          className="ml-0.5 text-[9px] text-slate-500 hover:text-[#FF3366] font-tab"
                          data-testid={`part-ramp-off-${i}`}
                        >×</button>
                      </>
                    ) : (
                      <button
                        onClick={() => setSongSections((s) => s.map((x, j) => j === i ? { ...x, ramp: Math.min(240, (x.bpm || bpm) + 20) } : x))}
                        className="text-[10px] font-tab text-slate-500 hover:text-[#c893ff] px-1"
                        data-testid={`part-ramp-on-${i}`}
                      >off</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[9px] text-slate-600 font-tab mt-1.5">Drag to reorder · each part can have its own repeats &amp; tempo. Time signature is locked while arranging.</p>
        </div>
      )}

      {presetsOpen && (
        <div className="mb-3 rounded-lg bg-[#0d1117] border border-white/10 p-2 max-h-56 overflow-y-auto scroll-thin" data-testid="presets-panel">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-tab uppercase tracking-widest text-[#00f0ff]">Your saved patterns</span>
            <button onClick={() => setPresetsOpen(false)} className="text-[10px] text-slate-500 hover:text-white font-tab" data-testid="presets-close">Close</button>
          </div>
          {savedTabs.length === 0 ? (
            <p className="text-[11px] text-slate-500 font-tab py-3 text-center">No saved patterns yet. Save one to reuse it here.</p>
          ) : (
            <div className="flex flex-col gap-1">
              {savedTabs.map((item) => (
                <button
                  key={item.id}
                  onClick={() => loadSavedTab(item)}
                  data-testid={`preset-tab-${item.id}`}
                  className="flex items-center justify-between gap-2 rounded bg-white/5 px-2 py-1.5 text-left hover:bg-white/10 transition-colors"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12px] font-semibold text-slate-200">{item.tab.title}</span>
                    <span className="block text-[9px] text-slate-500 font-tab">{item.tab.time_signature} · {item.tab.bpm} BPM{(item.tab.sections || []).length > 1 ? ` · ${item.tab.sections.length} parts` : ""}</span>
                  </span>
                  <FolderOpen className="w-3.5 h-3.5 text-[#00f0ff] shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <Button onClick={openPresets} variant="outline" className="w-full h-9 mb-2 border-[#00f0ff]/40 text-[#00f0ff] bg-[#00f0ff]/5" data-testid="load-preset-button">
        <FolderOpen className="w-4 h-4 mr-1.5" /> Load a saved pattern{!isPro ? " (Pro)" : ""}
      </Button>

      <div className="flex gap-2 mb-2">
        <Button onClick={addToSong} variant="outline" className="flex-1 h-9 border-[#ffab00]/40 text-[#ffab00] bg-[#ffab00]/5" data-testid="add-to-song-button">
          <Plus className="w-4 h-4 mr-1.5" /> Add to song
        </Button>
        {loadedTabId ? (
          <>
            <Button onClick={() => saveToLibrary(false)} variant="outline" className="flex-1 h-9 border-[#00f0ff]/40 text-[#00f0ff] bg-[#00f0ff]/5" data-testid="update-pattern-button">
              <Save className="w-4 h-4 mr-1.5" /> Update
            </Button>
            <Button onClick={() => saveToLibrary(true)} variant="outline" size="icon" className="h-9 w-9 border-white/15 bg-white/5" data-testid="save-as-new-button" title="Save as a new pattern">
              <Plus className="w-4 h-4" />
            </Button>
          </>
        ) : (
          <Button onClick={() => saveToLibrary(false)} variant="outline" className="flex-1 h-9 border-white/15 bg-white/5" data-testid="save-pattern-button">
            <Save className="w-4 h-4 mr-1.5" /> Save{!isPro ? " (Pro)" : ""}
          </Button>
        )}
      </div>

      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] text-slate-500 font-tab shrink-0">Export loops</span>
        <Select value={String(exportLoops)} onValueChange={(v) => setExportLoops(parseInt(v, 10))}>
          <SelectTrigger className="h-7 w-[72px] bg-white/5 border-white/15 text-xs font-tab" data-testid="export-loops-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#12161f] border-white/10">
            {[1, 2, 4, 8, 16].map((n) => (
              <SelectItem key={n} value={String(n)} data-testid={`export-loops-${n}`}>×{n}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-[9px] text-slate-600 font-tab">longer practice track</span>
        <button
          onClick={() => setFadeOut((f) => !f)}
          className={`ml-auto h-7 px-2.5 rounded text-[10px] font-tab border transition-colors ${fadeOut ? "bg-[#A855F7]/20 text-[#c893ff] border-[#A855F7]/40" : "border-white/15 text-slate-400 bg-white/5"}`}
          data-testid="fade-out-toggle"
          title="Gently fade out the end of the exported file"
        >
          {fadeOut ? "Fade ✓" : "Fade out"}
        </button>
      </div>

      <div className="flex gap-2 mb-2">
        <Button onClick={() => exportAudio("wav")} disabled={exporting} variant="outline" className="flex-1 h-9 border-[#A855F7]/40 text-[#c893ff] bg-[#A855F7]/5" data-testid="export-audio-button">
          <Download className="w-4 h-4 mr-1.5" /> {exporting ? "Rendering…" : `WAV${!isPro ? " (Pro)" : ""}`}
        </Button>
        <Button onClick={() => exportAudio("mp3")} disabled={exporting} variant="outline" className="flex-1 h-9 border-[#A855F7]/40 text-[#c893ff] bg-[#A855F7]/5" data-testid="export-mp3-button">
          <Download className="w-4 h-4 mr-1.5" /> {exporting ? "Rendering…" : `MP3${!isPro ? " (Pro)" : ""}`}
        </Button>
        <Button onClick={downloadPreviewCard} variant="outline" className="flex-1 h-9 border-white/15 bg-white/5 text-slate-300" data-testid="download-card-button">
          <ImageIcon className="w-4 h-4 mr-1.5" /> Card{!isPro ? " (Pro)" : ""}
        </Button>
      </div>

      <Button onClick={shareSong} variant="outline" className="w-full h-9 mb-2 border-[#00f0ff]/40 text-[#00f0ff] bg-[#00f0ff]/5" data-testid="share-song-button">
        <Share2 className="w-4 h-4 mr-1.5" /> Share song{!isPro ? " (Pro)" : ""}
      </Button>

      <Button onClick={openInPlayer} className="w-full h-9 bg-[#ffab00] text-black hover:bg-[#ffc233] font-semibold" data-testid="pattern-open-player-button">
        <FileMusic className="w-4 h-4 mr-2" /> Open in player (save &amp; export)
      </Button>

      <Dialog open={!!shareResult} onOpenChange={(v) => { if (!v) setShareResult(null); }}>
        <DialogContent className="bg-[#12161f] border-white/10 sm:max-w-sm" data-testid="share-qr-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase tracking-tight">Share your song</DialogTitle>
          </DialogHeader>
          {shareResult && (
            <div className="flex flex-col items-center gap-4 py-2">
              {shareResult.qr && (
                <img src={shareResult.qr} alt="Share QR code" className="w-48 h-48 rounded-lg bg-white p-2" data-testid="share-qr-image" />
              )}
              <p className="text-xs text-slate-400 font-tab text-center">Scan with a phone camera to open the song — no account needed. Shows a rich preview card when posted.</p>
              <div className="flex items-center gap-2 w-full">
                <Input readOnly value={shareResult.url} className="h-9 bg-[#0b0c10] border-white/10 text-xs font-tab" data-testid="share-url-input" />
                <Button onClick={copyShareLink} size="icon" variant="outline" className="h-9 w-9 shrink-0 border-white/15 bg-white/5" data-testid="copy-share-link">
                  <Check className="w-4 h-4" />
                </Button>
              </div>
              {recentShares.length > 0 && (
                <div className="w-full mt-1" data-testid="recent-shares">
                  <p className="text-[10px] font-tab uppercase tracking-widest text-slate-500 mb-1.5">Recent shares</p>
                  <div className="flex flex-col gap-1 max-h-32 overflow-y-auto scroll-thin">
                    {recentShares.map((r) => {
                      const link = `${window.location.origin}/api/share/${r.id}/view`;
                      return (
                        <div
                          key={r.id}
                          data-testid={`recent-share-${r.id}`}
                          className="flex items-center justify-between gap-2 rounded bg-white/5 px-2 py-1"
                        >
                          <span className="truncate text-[11px] font-tab text-slate-300 flex-1 min-w-0">{r.title}</span>
                          <span className="text-[9px] font-tab text-slate-500 shrink-0" data-testid={`share-views-${r.id}`} title="Times opened">{r.views || 0} views</span>
                          <button
                            onClick={async () => { try { await navigator.clipboard.writeText(link); toast.success("Link copied"); } catch (e) { /* ignore */ } }}
                            className="shrink-0" data-testid={`recent-share-copy-${r.id}`} title="Copy link"
                          >
                            <Copy className="w-3 h-3 text-slate-500 hover:text-[#00f0ff]" />
                          </button>
                          <button
                            onClick={async () => { try { await api.delete(`/share/${r.id}`); setRecentShares((xs) => xs.filter((x) => x.id !== r.id)); toast.success("Share deleted"); } catch (e) { toast.error("Could not delete"); } }}
                            className="shrink-0" data-testid={`recent-share-delete-${r.id}`} title="Delete share"
                          >
                            <Trash2 className="w-3 h-3 text-slate-500 hover:text-[#FF3366]" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
